<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import * as Sentry from '@sentry/vue';
import { useRouter } from 'vue-router';
import { storeToRefs } from 'pinia';
import { useAToast } from '@Atoms';
import useShoppingCartStore from '../../../../store';
import { CART_STEPS } from '../../../../enums';
import { CompleteOutcomeService } from '../../../../services';
import { useCartSteps } from '../../../../composables';
import { isCheckInIntakeType } from '../../../../helpers';

const emits = defineEmits<{
  onStepUpdate: [CART_STEPS];
}>();

const { cartData, completeOutcomeRoute, loading, isIntake, outcomeType } = storeToRefs(useShoppingCartStore());
const { outcomeCompletedCartReset } = useShoppingCartStore();

const { notify, TOAST_TYPES } = useAToast();
const { currentStep, previousStep } = useCartSteps();
const { push } = useRouter();

const checkoutPaidLoading = ref(false);

const isCheckInType = computed(() => isCheckInIntakeType(outcomeType.value));
const buttonLabel = computed(() =>
  // prettier-ignore
  isIntake.value && isCheckInType.value ? 'cart.label.completeCheckIn' 
    : isIntake.value ? 'cart.label.completeIntake' 
    : 'cart.label.completeCheckIn'
);

const TOAST_ERROR = {
  message: 'cart.toast.defaultError.error.message',
  title: 'cart.toast.defaultError.error.title',
  type: TOAST_TYPES.ERROR,
};

onMounted(() => (currentStep.value = CART_STEPS.COMPLETE_CHECK_IN));

function onCompleteCheckIn() {
  if (checkoutPaidLoading.value) return;

  // NOTE :: TS compliance - completeOutcomeRoute should always exist when completing an outcome
  if (!completeOutcomeRoute.value) {
    Sentry.captureException(new Error('completeOutcomeRoute is null during onCompleteCheckIn'), {
      tags: { component: 'MCartStepCompleteFlow', type: 'ts-compliance' },
    });
  }

  checkoutPaidLoading.value = true;

  const PAYLOAD = {
    ...cartData.value,
  };

  // NOTE :: Reset the lock only on error. On success we navigate away, and resetting it before the
  // async router push finishes would re-enable the button and allow a duplicate completion.
  CompleteOutcomeService(PAYLOAD)
    .then(() => push(completeOutcomeRoute.value!).then(() => outcomeCompletedCartReset()))
    .catch(() => notify(TOAST_ERROR) && (checkoutPaidLoading.value = false));
}
</script>

<template>
  <div class="flex flex-col gap-4">
    <footer class="flex justify-between pt-4">
      <Button
        v-if="previousStep"
        outlined
        class="af-button af-button-success-outlined w-fit"
        icon="pi pi-arrow-left"
        :label="$t('common.back')"
        @click="emits('onStepUpdate', previousStep)"
      />

      <div class="flex w-full justify-end">
        <Button
          class="af-button af-button-success"
          iconPos="right"
          :disabled="loading || checkoutPaidLoading"
          :loading="loading || checkoutPaidLoading"
          :label="$t(buttonLabel)"
          @click="onCompleteCheckIn"
        >
          <template #icon>
            <FaIcon
              class="mr-2"
              :icon="$icons.faPaw"
            />
          </template>
        </Button>
      </div>
    </footer>
  </div>
</template>
